import { ExploreFilter } from "@/modules/explore/exploreTypes"
import { PriceObjectDtoSchema } from "@modules/common/priceObject/gateway/priceObjectApiDto"
import { HUImageDtoSchema } from "@modules/dtos/imageDto"
import { CoachDtoSchema } from "@modules/profile/coachProfile/coachDto"
import { UserDtoSchema } from "@modules/profile/userProfile/userDto"
import { UserProgramItemDtoSchema } from "@modules/programs/gateway/userProgramItemDto"
import { ProgramVisibility } from "@modules/programs/types/programTypes"
import { z } from "zod"

export const ProgramBundleTypeOf = "PROGRAM_BUNDLE"

export enum ProgramBundleType {
  ON_OFF = "ON_OFF",
  VIRTUAL = "VIRTUAL",
}

export const PROGRAM_BUNDLE_NAME_MAX_LENGTH = 250
export const PROGRAM_BUNDLE_DESCRIPTION_MAX_LENGTH = 65530

export const LightProgramBundleInfoDtoSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    image: HUImageDtoSchema.nullable(),
    price: PriceObjectDtoSchema.nullable(),
    type: z.lazy(() => z.enum(ProgramBundleType)),
  })
  .meta({ id: "LightProgramBundleInfoDtoSchema" })

export const ProgramBundleDtoSchema = z
  .object({
    id: z.string(),
    type: z.lazy(() => z.enum(ProgramBundleType)),
    name: z.string(),
    description: z.string(),
    visibility: z.lazy(() => z.enum(ProgramVisibility)),
    image: HUImageDtoSchema.nullable(),
    invitationLink: z.string().nullable(),
    owner: CoachDtoSchema,
    subscriptionCount: z.number(),
    price: PriceObjectDtoSchema.nullable(),
    programs: z.array(z.lazy(() => UserProgramItemDtoSchema)),
    nbPrograms: z.number(),
    trialDuration: z.number().nullable(),
    isEligibleToTrial: z.boolean().nullable(),
  })
  .meta({ id: "ProgramBundleDtoSchema" })

export const ProgramBundleDraftDtoSchema = z
  .object({
    name: z.string().max(PROGRAM_BUNDLE_NAME_MAX_LENGTH),
    description: z.string().max(PROGRAM_BUNDLE_DESCRIPTION_MAX_LENGTH),
    visibility: z.lazy(() => z.enum(ProgramVisibility)),
    type: z.lazy(() => z.enum(ProgramBundleType)),
    price: PriceObjectDtoSchema.nullable(),
    imageKey: z.string().nullable(),
    programIds: z.array(z.string()),
    trialDuration: z.number().nullable(),
  })
  .meta({ id: "ProgramBundleDraftDtoSchema" })

export const ProgramBundleLightDtoSchema = z
  .object({
    programBundleId: z.string(),
    programBundleName: z.string(),
    paidPrice: PriceObjectDtoSchema.nullable(),
    referencePrice: PriceObjectDtoSchema.nullable(),
  })
  .meta({ id: "ProgramBundleLightDtoSchema" })

export const ProgramBundleItemDtoSchema = z
  .object({
    id: z.string(),
    type: z.lazy(() => z.enum(ProgramBundleType)),
    name: z.string(),
    image: HUImageDtoSchema.nullable(),
    owner: UserDtoSchema, // In back it's nullable but in front it's not, so we need to handle it
    visibility: z.lazy(() => z.enum(ProgramVisibility)),
    subscribed: z.boolean(),
    subscriptionCount: z.number(),
    price: PriceObjectDtoSchema.nullable(),
    nbPrograms: z.number(),
    exploreType: z.literal(ExploreFilter.PROGRAM_BUNDLE),
  })
  .meta({ id: "ProgramBundleItemDtoSchema" })

export type ProgramBundleItemDto = z.infer<typeof ProgramBundleItemDtoSchema>
export type ProgramBundleDraftDto = z.infer<typeof ProgramBundleDraftDtoSchema>
export type ProgramBundleDto = z.infer<typeof ProgramBundleDtoSchema>
export type ProgramBundleLightDto = z.infer<typeof ProgramBundleLightDtoSchema>
export type LightProgramBundleInfoDto = z.infer<typeof LightProgramBundleInfoDtoSchema>
